import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, QrCode, Download, Building, User, Plane, Calendar,
  CirclePoundSterling, Users, CreditCard, FileExclamationPoint, Palette,
  ListCheck, ScrollText, Car, Mountain
} from 'lucide-react';
import { useTranslation } from '../../../../i18n/i18n';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import api from '../../../../api/axios';

// Importer le composant InteractifGen
import VoucherCustomizer from './VoucherCustomizer';

import VoucherFlight from './EditFlight';
import VoucherHotel from './EditHotel';
import VoucherTransfer from './EditTransfer';
import VoucherActivity from './EditActivity';

const VoucherEditPage = ({ lang }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(lang);

  const [voucherData, setVoucherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [voucherSections, setVoucherSections] = useState([]);
  const [voucherConfig, setVoucherConfig] = useState(null);
  const [type, setType] = useState(null);

  const booking_url = process.env.REACT_APP_API_BOOKING_URL;

  // Références pour chaque section
  const voucherRefs = {
    header: useRef(null),
    bookingInfo: useRef(null),
    passengers: useRef(null),
    flightInfo: useRef(null),
    providerInfo: useRef(null),
    holderInfo: useRef(null),
    qrCode: useRef(null),
    notes: useRef(null),
    footer: useRef(null)
  };

  const fetchVoucherDetail = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`${booking_url}/${id}`);

      // Vérifier si la réponse a la nouvelle structure (avec result)
      if (data.result) {
        const transformedData = transformBookingData(data.result);
        setVoucherData(transformedData);
        setType(data.result.bookingType);
      } else {
        // Ancienne structure
        setVoucherData(data);
        setType(data.service);
      }

    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
    } finally {
      setLoading(false);
    }
  };

  const transformBookingData = (newBooking) => {
    return {
      // Basic booking info
      booking_number: newBooking.bookingNumber,
      bookingNumber: newBooking.bookingNumber,
      status: newBooking.status,
      payment_status: newBooking.paymentStatus,
      service: newBooking.bookingType,
      amount_total: newBooking.amountTotal,
      currency: newBooking.currency,
      provider_amount_total: newBooking.providerAmountTotal,
      provider_currency: newBooking.providerCurrency,
      checkin: newBooking.checkin,
      checkout: newBooking.checkout,
      duration: newBooking.duration,
      created_at: newBooking.confirmedDate,
      cancelled_at: newBooking.cancelledDate,
      payment_date_limit_at: newBooking.paymentDateLimit,
      date_limit_cancellation: newBooking.dateLimitCcancellation,

      // Client information
      holder: newBooking.holder,
      customer: newBooking.customer,
      customer_id: newBooking.customer?.id,
      customer_account_id: newBooking.customer?.id,

      // Provider information
      provider_id: newBooking.provider?.id,
      provider: newBooking.provider,

      // Product information
      product_id: newBooking.product?.id,
      product: newBooking.product,
      country: newBooking.country,
      destination: newBooking.product?.city,
      country_id: newBooking.country?.id,
      destination_id: newBooking.product?.city?.id,

      // Flight specific (if applicable)
      booking_flight: newBooking.flight ? {
        trip: newBooking.flight.trip,
        adult: newBooking.flight.adult,
        child: newBooking.flight.child,
        infant: newBooking.flight.infant,
        airline: newBooking.flight.airline,
        itineraries: newBooking.flight.itinerary?.map(it => ({
          departure: it.departure,
          arrival: it.arrival,
          segments: it.segments,
          duration: it.segments?.[0]?.duration
        })),
      } : null,

      booking_transfer: newBooking.transfer ? {
        type: newBooking.transfer.type,
        adult: newBooking.transfer.adult,
        child: newBooking.transfer.child,
        itineraries: newBooking.transfer.itinerary?.map(it => ({
          origin: it.origin,
          destination: it.destination,
          vehicle: it.vehicle,
          transferDate: it.transferDate,
          transferTime: it.transferTime,
          travelType: it.travelType,
          travelNumber: it.travelNumber
        }))
      } : null,

      booking_activity: newBooking.activity ? {
        days: newBooking.activity.days,
        adult: newBooking.activity.adult,
        child: newBooking.activity.child,
        baby: newBooking.activity.baby,
        senior: newBooking.activity.senior,
        amount: newBooking.activity.amount,
        prices: newBooking.activity.prices
      } : null,

      booking_hotel: newBooking.hotel ? {
        meal_name: newBooking.hotel.meal_name,
        meal_id: newBooking.hotel.meal_id,
        meal_plan: newBooking.hotel.meal_name,
        nights: newBooking.hotel.nights,
        rooms_count: newBooking.hotel.rooms_count,
        adult_count: newBooking.hotel.adult_count,
        child_count: newBooking.hotel.child_count,
        rooms: newBooking.hotel.rooms?.map((room, index) => ({
          id: room.id || index,
          name: room.name,
          bed_type: room.bed_type,
          count: room.count,
          adult: room.adult,
          child: room.child,
          amount: room.amount,
          amount_provider: room.amountProvider || room.prices?.[0]?.amountProvider,
          product_hotel_room_id: room.id || room.room_id,
          prices: room.prices?.map(price => ({
            date: price.date,
            amount: price.amount,
            amountProvider: price.amountProvider
          })),
          is_obsolete: room.is_obsolete || false
        })),
      } : null,

      // Passengers
      passengers: newBooking.passengers?.map(p => ({
        title: p.title,
        type: p.type,
        gender: p.gender,
        last_name: p.lastName,
        first_name: p.firstName,
        is_holder: `${p.firstName} ${p.lastName}` === newBooking.holder
      })),

      // Policies and conditions
      cancellation_policies: newBooking.cancellationPolicies?.map(cp => ({
        date: cp.date,
        amount: cp.amount,
        amountProvider: cp.amountProvider
      })),

      policies: newBooking.rateComment?.map(rc => ({
        value: rc.value
      })),

      // Additional data
      comments: newBooking.comments || [],
      extra_includes: newBooking.extraIncludes?.map(ei => ({
        value: ei.value
      })) || [],

      extra_excludes: newBooking.extraExclude?.map(ee => ({
        value: ee.value
      })) || []
    };
  };

  useEffect(() => {
    fetchVoucherDetail();
  }, [id]);


  const captureVoucherSections = async () => {
    try {
      const sectionsData = [];

      sectionsData.push({
        id: 'header',
        name: t('voucherHeader'),
        type: 'header',
        title: t('extranetVoucher'),
        bookingNumber: voucherData.booking_number,
        status: voucherData.status,
        statusColor: getStatusColor(voucherData.status),
        component: 'HeaderSection'
      });

      sectionsData.push({
        id: 'bookingInfo',
        name: t('bookingInformation'),
        type: 'info',
        serviceType: voucherData.service,
        serviceIcon: getServiceIcon(voucherData.service),
        checkin: formatDate(voucherData.checkin),
        checkout: formatDate(voucherData.checkout),
        totalAmount: voucherData.amount_total?.toLocaleString(),
        currency: voucherData.currency,
        paymentStatus: voucherData.payment_status,
        bookingDate: formatDateTime(voucherData.created_at),
        component: 'BookingInfoSection'
      });

      if (voucherData.passengers?.length > 0) {
        sectionsData.push({
          id: 'passengers',
          name: t('passengerInformation'),
          type: 'table',
          passengers: voucherData.passengers,
          columns: [
            { key: 'name', label: t('name') },
            { key: 'gender', label: t('gender') },
            { key: 'type', label: t('type') },
            { key: 'birthDate', label: t('birthDate') }
          ],
          component: 'PassengersSection'
        });
      }

      // Services Inclusions & Exclusions
      if ((voucherData.extra_includes && voucherData.extra_includes.length > 0) ||
        (voucherData.extra_excludes && voucherData.extra_excludes.length > 0)) {
        sectionsData.push({
          id: 'servicesInclusionsExclusions',
          name: t('servicesInclusionsExclusions'),
          type: 'services',
          extra_includes: voucherData.extra_includes,
          extra_excludes: voucherData.extra_excludes,
          component: 'ServicesSection'
        });
      }

      // Politiques d'annulation
      if (voucherData.cancellation_policies && voucherData.cancellation_policies.length > 0) {
        sectionsData.push({
          id: 'cancellationPolicies',
          name: t('cancellationPolicies'),
          type: 'policies',
          cancellation_policies: voucherData.cancellation_policies,
          currency: voucherData.currency,
          provider_currency: voucherData.provider_currency,
          component: 'CancellationPoliciesSection'
        });
      }

      // Informations de contact
      if (voucherData.product || voucherData.customer || voucherData.provider) {
        sectionsData.push({
          id: 'contactInformation',
          name: t('contactInformation'),
          type: 'contact',
          product: voucherData.product,
          customer: voucherData.customer,
          provider: voucherData.provider,
          component: 'ContactInformationSection'
        });
      }

      if (type === 'flight') {
        sectionsData.push({
          id: 'FlightInfoSection',
          name: t('flightInformation'),
          type: 'flight',
          airline: voucherData.booking_flight.airline?.name || voucherData.booking_flight.airline_code,
          trip: voucherData.booking_flight.trip,
          adult: voucherData.booking_flight.adult,
          child: voucherData.booking_flight.child,
          infant: voucherData.booking_flight.infant,
          itineraries: voucherData.booking_flight.itineraries?.map(it => ({
            departure: {
              code: it.departure?.code || it.departure_airport_code,
              name: it.departure?.name,
              date: it.departure?.date,
              terminal: it.departure?.terminal
            },
            arrival: {
              code: it.arrival?.code || it.arrival_airport_code,
              name: it.arrival?.name,
              date: it.arrival?.date,
              terminal: it.arrival?.terminal
            },
            segments: it.segments?.map(segment => ({
              airline: {
                code: segment.airline?.code,
                name: segment.airline?.name
              },
              flightNumber: segment.flightNumber,
              duration: segment.duration,
              departure: {
                code: segment.departure?.code || segment.departure_airport_code,
                name: segment.departure?.name,
                date: segment.departure?.date,
                terminal: segment.departure?.terminal
              },
              arrival: {
                code: segment.arrival?.code || segment.arrival_airport_code,
                name: segment.arrival?.name,
                date: segment.arrival?.date,
                terminal: segment.arrival?.terminal
              }
            }))
          })) || [],
          departure_airport_code: voucherData.booking_flight.itineraries?.[0]?.departure_airport_code,
          arrival_airport_code: voucherData.booking_flight.itineraries?.[0]?.arrival_airport_code,
          duration: voucherData.booking_flight.itineraries?.[0]?.duration,
          component: 'FlightInfoSection'
        });
      }

      // Ajouter la section Transfer si elle existe
      if (type === 'transfer') {
        sectionsData.push({
          id: 'TransferSection',
          name: t('transferInformation'),
          type: 'transfer',
          booking_transfer: {
            type: voucherData.booking_transfer.type, // 'Oneway' ou 'Roundtrip'
            adult: voucherData.booking_transfer.adult || 0,
            child: voucherData.booking_transfer.child || 0,
            infant: voucherData.booking_transfer.infant || 0,
            senior: voucherData.booking_transfer.senior || 0,
            baby: voucherData.booking_transfer.baby || 0,
            itineraries: voucherData.booking_transfer.itineraries?.map(it => ({
              transferDate: it.transferDate,
              transferTime: it.transferTime,
              travelType: it.travelType, // 'flight', 'train', etc.
              travelNumber: it.travelNumber,
              origin: {
                name: it.origin?.name,
                type: it.origin?.type // 'airport', 'station', 'hotel', etc.
              },
              destination: it.destination?.map(dest => ({
                name: dest.name,
                type: dest.type
              })) || [],
              vehicle: it.vehicle ? {
                type: it.vehicle.type,
                brand: it.vehicle.brand,
                model: it.vehicle.model,
                luggageCapacity: it.vehicle.luggageCapacity,
                capacity: it.vehicle.capacity
              } : null
            })) || []
          },
          lang: lang, // Pour le formatage des dates
          component: 'TransferSection'
        });
      }

      // Ajouter la section Activity si elle existe
      if (type === 'activity') {
        sectionsData.push({
          id: 'ActivitySection',
          name: t('activityInformation'),
          type: 'activity',
          booking_activity: {
            adult: voucherData.booking_activity.adult || 0,
            child: voucherData.booking_activity.child || 0,
            baby: voucherData.booking_activity.baby || 0,
            senior: voucherData.booking_activity.senior || 0,
            days: voucherData.booking_activity.days || 1,
            amount: voucherData.booking_activity.amount || 0,
            prices: voucherData.booking_activity.prices || []
          },
          product: voucherData.product ? {
            name: voucherData.product.name,
            country: voucherData.product.country,
            city: voucherData.product.city,
            phone: voucherData.product.phone,
            address: voucherData.product.address,
            latitude: voucherData.product.latitude,
            longitude: voucherData.product.longitude
          } : null,
          currency: voucherData.currency || 'EUR',
          component: 'ActivitySection'
        });
      };

      if (type === 'hotel') {
  sectionsData.push({
    id: 'HotelSection',
    name: t('hotelInformation'),
    type: 'hotel',
    booking_hotel: {
      adult_count: voucherData.booking_hotel?.adult_count || 0,
      child_count: voucherData.booking_hotel?.child_count || 0,
      rooms_count: voucherData.booking_hotel?.rooms_count || 0,
      nights: voucherData.booking_hotel?.nights || voucherData.duration || 0,
      meal_name: voucherData.booking_hotel?.meal_name || '',
      rooms: voucherData.booking_hotel?.rooms?.map(room => ({
        name: room.name,
        bed_type: room.bed_type,
        count: room.count || 1,
        adult: room.adult || 0,
        child: room.child || 0,
        amount: room.amount || 0,
        amount_provider: room.amount_provider || room.prices?.[0]?.amountProvider,
        product_hotel_room_id: room.product_hotel_room_id,
        prices: room.prices || []
      })) || [],
      created_at: voucherData.booking_hotel?.created_at,
      updated_at: voucherData.booking_hotel?.updated_at
    },
    product: voucherData.product ? {
      name: voucherData.product.name,
      country: voucherData.product.country,
      city: voucherData.product.city,
      phone: voucherData.product.phone,
      address: voucherData.product.address,
      latitude: voucherData.product.latitude,
      longitude: voucherData.product.longitude
    } : null,
    checkin: voucherData.checkin,
    checkout: voucherData.checkout,
    duration: voucherData.duration,
    currency: voucherData.currency || 'EUR',
    provider_currency: voucherData.provider_currency || 'EUR',
    policies: voucherData.policies || [],
    extra_includes: voucherData.extra_includes || [],
    extra_excludes: voucherData.extra_excludes || [],
    component: 'HotelSection'
  });
}

      sectionsData.push({
        id: 'qrCode',
        name: t('qrCode'),
        type: 'qrcode',
        component: 'QrCodeSection'
      });

      sectionsData.push({
        id: 'notes',
        name: t('importantNotes'),
        type: 'notes',
        notes: [
          t('notePresentVoucher'),
          t('noteKeepVoucher'),
          t('noteEmergencyContact'),
          t('noteCancellationPolicies')
        ],
        generationDate: formatDateTime(new Date()),
        component: 'NotesSection'
      });

      sectionsData.push({
        id: 'footer',
        name: t('footer'),
        type: 'footer',
        message: t('officialVoucherMessage'),
        generatedBy: 'IDH EXTRANET System',
        component: 'FooterSection'
      });

      const config = {
        layout: 'vertical',
        columns: 1,
        gap: 32,
        padding: 40,
        fontSize: 16,
        primaryColor: '#7c3aed',
        secondaryColor: '#6b7280',
        backgroundColor: '#ffffff',
        borderRadius: 12,
        showLogo: true,
        logoPosition: 'top-left'
      };

      setVoucherSections(sectionsData);
      setVoucherConfig(config);
      setShowCustomizer(true);

      toast.success(t('voucherReadyForCustomization'));
    } catch (error) {
      console.error('Error preparing voucher sections:', error);
      toast.error(t('customizationError'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('notAvailable');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : lang === 'es' ? 'es-ES' : 'en-US');
    } catch {
      return t('notAvailable');
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return t('notAvailable');
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : lang === 'es' ? 'es-ES' : 'en-US') +
        ' ' + date.toLocaleTimeString(lang === 'fr' ? 'fr-FR' : lang === 'es' ? 'es-ES' : 'en-US', {
          hour: '2-digit',
          minute: '2-digit'
        });
    } catch {
      return t('notAvailable');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
      case 'paid':
      case 'active':
        return '#10b981';
      case 'cancelled':
      case 'refused':
      case 'expired':
        return '#ef4444';
      case 'pending':
      case 'unpaid':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const getServiceIcon = (service) => {
    switch (service) {
      case 'flight': return <Plane size={20} />;
      case 'hotel': return <Building size={20} />;
      case 'activity': return <Mountain size={20} />;
      case 'transfer': return <Car size={20} />;
      default: return <Building size={20} />;
    }
  };

  const generatePDF = async () => {
    try {
      toast.info(t('generatingPDF'));

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const margin = 10;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;

      let currentY = margin;

      const sections = document.querySelectorAll('.pdf-section');

      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];

        const canvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff'
        });

        const imgData = canvas.toDataURL('image/png');
        const imgHeight = (canvas.height * usableWidth) / canvas.width;

        if (currentY + imgHeight > pageHeight - margin + 6) {
          pdf.addPage();
          currentY = margin;
        }

        pdf.addImage(
          imgData,
          'PNG',
          margin,
          currentY,
          usableWidth,
          imgHeight
        );

        currentY += imgHeight + 5;
      }

      pdf.save(`voucher-${voucherData?.booking_number || 'unknown'}.pdf`);
      toast.success(t('pdfGenerated'));
    } catch (error) {
      console.error(error);
      toast.error(t('pdfError'));
    }
  };

  // Render product-specific component based on type
  const renderProductSpecificSection = () => {
    const props = { voucherData, t, formatDate, formatDateTime, styles, lang };

    switch (type) {
      case 'flight':
        return <VoucherFlight {...props} />;
      case 'hotel':
        return <VoucherHotel {...props} />;
      case 'transfer':
        return <VoucherTransfer {...props} />;
      case 'activity':
        return <VoucherActivity {...props} />;
      default:
        return null;
    }
  };

  const styles = {
    container: {
      padding: '20px 40px 20px',
      background: '#f5f7fa',
      minHeight: '100vh',
      color: '#333'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      marginBottom: '32px'
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      backgroundColor: 'white',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      color: '#4b5563',
      transition: 'all 0.2s ease',
      marginBottom: '0px',
      width: 'fit-content',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
    },
    pageTitle: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    actionButtons: {
      display: 'flex',
      gap: '12px',
      marginLeft: 'auto'
    },
    actionButton: {
      padding: '10px 20px',
      background: '#7c3aed',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500',
      transition: 'all 0.2s ease'
    },
    customizerButton: {
      padding: '10px 20px',
      background: '#f59e0b', // Orange pour le distinguer
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500',
      transition: 'all 0.2s ease'
    },
    voucherContainer: {
      maxWidth: '800px',
      margin: '0 auto',
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
      padding: '40px',
      position: 'relative'
    },
    voucherHeader: {
      textAlign: 'center',
      marginBottom: '32px',
      borderBottom: '2px solid #e5e7eb',
      paddingBottom: '24px'
    },
    voucherTitle: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#7c3aed',
      margin: '0 0 16px 0',
      letterSpacing: '2px'
    },
    bookingNumber: {
      fontSize: '18px',
      color: '#6b7280',
      marginBottom: '16px',
      fontWeight: '500'
    },
    statusBadge: {
      display: 'inline-block',
      padding: '8px 24px',
      borderRadius: '20px',
      fontSize: '14px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '1px'
    },
    section: {
      marginBottom: '32px',
      paddingBottom: '24px',
      borderBottom: '1px solid #e5e7eb'
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '20px',
      paddingBottom: '8px',
      borderBottom: '2px solid #7c3aed',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '20px'
    },
    infoItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    infoLabel: {
      fontSize: '14px',
      color: '#6b7280',
      fontWeight: '500'
    },
    infoValue: {
      fontSize: '16px',
      color: '#1f2937',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '16px'
    },
    tableHeader: {
      background: '#f8fafc',
      borderBottom: '2px solid #e5e7eb'
    },
    tableHeaderCell: {
      padding: '12px 16px',
      textAlign: 'left',
      fontWeight: '600',
      color: '#374151',
      fontSize: '14px',
      textTransform: 'uppercase'
    },
    tableRow: {
      borderBottom: '1px solid #e5e7eb'
    },
    tableCell: {
      padding: '12px 16px',
      fontSize: '14px',
      color: '#374151'
    },
    qrContainer: {
      textAlign: 'center',
      margin: '32px 0',
      padding: '24px',
      background: '#f8fafc',
      borderRadius: '8px',
      border: '1px dashed #d1d5db'
    },
    qrPlaceholder: {
      width: '150px',
      height: '150px',
      background: '#e5e7eb',
      borderRadius: '8px',
      margin: '0 auto 16px auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#6b7280'
    },
    notes: {
      fontSize: '12px',
      color: '#6b7280',
      lineHeight: '1.6',
      marginTop: '16px'
    },
    noteItem: {
      marginBottom: '8px'
    },
    footer: {
      marginTop: '40px',
      paddingTop: '24px',
      borderTop: '1px solid #e5e7eb',
      textAlign: 'center',
      fontSize: '12px',
      color: '#9ca3af',
      lineHeight: '1.6'
    },
    loading: {
      textAlign: 'center',
      padding: '60px',
      color: '#6b7280'
    }
  };

  if (loading) {
    return <div style={styles.loading}>{t('loading')}...</div>;
  }

  if (!voucherData) {
    return <div style={styles.loading}>{t('noData')}</div>;
  }

  return (
    <div>
      <div style={styles.container}>
        {/* Header avec boutons */}
        <div style={styles.header}>
          <button
            style={styles.backButton}
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={20} />
            {t('backToDetails')}
          </button>
          <h1 style={styles.pageTitle}>{t(`${type}Voucher`)}</h1>
          <div style={styles.actionButtons}>
            {/* Bouton Personnaliser */}
            <button
              style={styles.customizerButton}
              onClick={captureVoucherSections}
              title={t('customizeVoucher')}
            >
              <Palette size={16} />
              {t('customize')}
            </button>

            <button style={styles.actionButton} onClick={generatePDF}>
              <Download size={16} />
              {t('downloadPDF')}
            </button>
          </div>
        </div>

        {/* Contenu du voucher avec id pour PDF */}
        <div id="voucher-content" style={styles.voucherContainer}>
          {/* Header du voucher avec ref */}
          <div className="pdf-section">
            <div ref={voucherRefs.header}>
              <div style={styles.voucherHeader}>
                <h1 style={styles.voucherTitle}>{t('extranetVoucher')}</h1>
                <div style={styles.bookingNumber}>
                  {t('bookingReference')}: {voucherData.booking_number}
                </div>
                <div style={{
                  ...styles.statusBadge,
                  background: getStatusColor(voucherData.status),
                  color: 'white'
                }}>
                  {t(voucherData.status?.toLowerCase()) || voucherData.status}
                </div>
              </div>
            </div>
          </div>

          {/* Informations de réservation avec ref */}
          <div className="pdf-section">
            <div ref={voucherRefs.bookingInfo} style={styles.section}>
              <div style={styles.sectionTitle}>
                <Calendar size={20} />
                {t('bookingInformation')}
              </div>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>{t('serviceType')}</div>
                  <div style={styles.infoValue}>
                    {getServiceIcon(voucherData.service)}
                    {t(voucherData.service)}
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>{t('checkinDate')}</div>
                  <div style={styles.infoValue}>
                    <Calendar size={16} />
                    {formatDate(voucherData.checkin)}
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>{t('checkoutDate')}</div>
                  <div style={styles.infoValue}>
                    <Calendar size={16} />
                    {formatDate(voucherData.checkout)}
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>{t('totalAmount')}</div>
                  <div style={styles.infoValue}>
                    <CirclePoundSterling size={16} />
                    {voucherData.amount_total?.toLocaleString()} {voucherData.currency}
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>{t('paymentStatus')}</div>
                  <div style={styles.infoValue}>
                    <CreditCard size={16} />
                    {t(voucherData.payment_status?.toLowerCase()) || voucherData.payment_status}
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>{t('bookingDate')}</div>
                  <div style={styles.infoValue}>
                    <Calendar size={16} />
                    {formatDateTime(voucherData.created_at)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Informations passagers avec ref */}
          {voucherData.passengers && voucherData.passengers.length > 0 && (
            <div className="pdf-section">
              <div ref={voucherRefs.passengers} style={styles.section}>
                <div style={styles.sectionTitle}>
                  <Users size={20} />
                  {t('passengerInformation')}
                </div>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    <th style={styles.tableHeaderCell}>{t('name')}</th>
                    <th style={styles.tableHeaderCell}>{t('gender')}</th>
                    <th style={styles.tableHeaderCell}>{t('type')}</th>

                  </tr>
                </thead>
                <tbody>
                  {voucherData.passengers.map((passenger, index) => (
                    <tr key={index} style={styles.tableRow}>
                      <td style={styles.tableCell}>
                        {passenger.first_name} {passenger.last_name}
                      </td>
                      <td style={styles.tableCell}>{passenger.gender}</td>
                      <td style={styles.tableCell}>{passenger.type}</td>

                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Section spécifique au type de service */}
          {type && (
            <>

              {renderProductSpecificSection()}
            </>
          )}

          {/* Services Inclusions & Exclusions */}
          {(voucherData.extra_includes && voucherData.extra_includes.length > 0) ||
            (voucherData.extra_excludes && voucherData.extra_excludes.length > 0) ? (
            <div className="pdf-section">
              <div style={styles.section}>
                <div style={styles.sectionTitle}>
                  <ListCheck size={20} />
                  {t('servicesInclusionsExclusions')}
                </div>
                <div style={styles.infoGrid}>
                  {/* Included Services */}
                  {voucherData.extra_includes && voucherData.extra_includes.length > 0 && (
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px' }}>
                        {t('included')}
                      </div>
                      <div style={styles.notes}>
                        {voucherData.extra_includes.map((service, index) => (
                          <p key={index} style={styles.noteItem}>• {service.value}</p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Excluded Services */}
                  {voucherData.extra_excludes && voucherData.extra_excludes.length > 0 && (
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px' }}>
                        {t('excluded')}
                      </div>
                      <div style={styles.notes}>
                        {voucherData.extra_excludes.map((service, index) => (
                          <p key={index} style={styles.noteItem}>• {service.value}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* Politiques d'annulation */}
          {voucherData.cancellation_policies && voucherData.cancellation_policies.length > 0 ? (
            <div className="pdf-section">
              <div style={styles.section}>
                <div style={styles.sectionTitle}>
                  <ScrollText size={20} />
                  {t('cancellationPolicies')}
                </div>
                {voucherData.cancellation_policies.map((policy, index) => (
                  <div key={index} style={styles.infoGrid}>
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>{t('deadline')}</div>
                      <div style={styles.infoValue}>
                        {formatDateTime(policy.date)}
                      </div>
                    </div>

                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>{t('fees')}</div>
                      <div style={styles.infoValue}>
                        {policy.amount} {voucherData.currency}
                      </div>
                    </div>

                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>{t('providerFees')}</div>
                      <div style={styles.infoValue}>
                        {policy.amountProvider} {voucherData.provider_currency}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Informations de contact */}
          {(voucherData.product || voucherData.customer || voucherData.provider) && (
            <div className="pdf-section">
              <div style={styles.section}>
                <div style={styles.sectionTitle}>
                  <User size={20} />
                  {t('contactInformation')}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginTop: '20px' }}>
                  {/* Section Produit */}
                  {voucherData.product && (
                    <div style={{
                      padding: '16px',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        marginBottom: '12px',
                        textAlign: 'center'
                      }}>
                        {t('productInfo')}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {voucherData.product.name && (
                          <div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                              {t('product')}
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                              {voucherData.product.name}
                            </div>
                          </div>
                        )}
                        {voucherData.product.phone && (
                          <div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                              {t('phone')}
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                              {voucherData.product.phone}
                            </div>
                          </div>
                        )}
                        {voucherData.product.address && (
                          <div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                              {t('address')}
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                              {voucherData.product.address}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Section Client */}
                  {voucherData.customer && (
                    <div style={{
                      padding: '16px',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        marginBottom: '12px',
                        textAlign: 'center'
                      }}>
                        {t('customer')}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {voucherData.customer.name && (
                          <div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                              {t('customer')}
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                              {voucherData.customer.name}
                            </div>
                          </div>
                        )}
                        {voucherData.customer.phone && (
                          <div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                              {t('phone')}
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                              {voucherData.customer.phone}
                            </div>
                          </div>
                        )}
                        {voucherData.customer.address && (
                          <div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                              {t('address')}
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                              {voucherData.customer.address}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Section Fournisseur */}
                  {voucherData.provider && (
                    <div style={{
                      padding: '16px',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        marginBottom: '12px',
                        textAlign: 'center'
                      }}>
                        {t('provider')}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {voucherData.provider.name || voucherData.provider.email && (
                          <div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                              {t('provider')}
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                              {voucherData.provider?.name ?? voucherData.provider?.email}
                            </div>
                          </div>
                        )}
                        {voucherData.provider.phone && (
                          <div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                              {t('phone')}
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                              {voucherData.provider.phone}
                            </div>
                          </div>
                        )}
                        {voucherData.provider.address && (
                          <div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                              {t('address')}
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                              {voucherData.provider.address}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* QR Code avec ref */}
          <div className="pdf-section">
            <div ref={voucherRefs.qrCode} style={styles.section}>
              <div style={styles.sectionTitle}>
                <QrCode size={20} />
                {t('qrCode')}
              </div>
              <div style={styles.qrContainer}>
                <div style={styles.qrPlaceholder}>
                  <QrCode size={64} color="#9ca3af" />
                </div>
                <div style={styles.infoLabel}>{t('scanQRCode')}</div>
              </div>
            </div>
          </div>

          {/* Notes importantes avec ref */}
          <div className="pdf-section">
            <div ref={voucherRefs.notes} style={styles.section}>
              <div style={styles.sectionTitle}>
                <FileExclamationPoint size={20} />
                {t('importantNotes')}
              </div>
              <div style={styles.notes}>
                <p style={styles.noteItem}>• {t('notePresentVoucher')}</p>
                <p style={styles.noteItem}>• {t('noteKeepVoucher')}</p>
                <p style={styles.noteItem}>• {t('noteEmergencyContact')}</p>
                <p style={styles.noteItem}>• {t('noteCancellationPolicies')}</p>
                <p style={styles.noteItem}>• {t('voucherGeneratedOn')}: {formatDateTime(new Date())}</p>
              </div>
            </div>
          </div>

          {/* Footer avec ref */}
          <div className="pdf-section">
            <div ref={voucherRefs.footer}>
              <div style={styles.footer}>
                <p>{t('officialVoucherMessage')}</p>
                <p>{t('generatedBy')} IDH EXTRANET System</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCustomizer && voucherConfig && (
        <VoucherCustomizer
          onClose={() => setShowCustomizer(false)}
          onSave={(configuration) => {
            console.log('Configuration sauvegardée:', configuration);
            setShowCustomizer(false);
            // Sauvegarder en BD
          }}
          initialSections={voucherSections}
          initialConfig={voucherConfig}
          lang={lang}
          // Passez les fonctions utilitaires
          getServiceIcon={getServiceIcon}
          formatDate={formatDate}
          formatDateTime={formatDateTime}
          getStatusColor={getStatusColor}
        />
      )}
    </div>
  );
};

export default VoucherEditPage;